import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getConsentAuditLog } from '@/api/client';

interface AuditEvent {
  eventType: string;
  previousStatus: string;
  newStatus: string;
  messageText?: string | null;
  triggeredBy: string;
  businessUserId?: string | null;
  timestamp: string;
}

interface ConsentAuditLogProps {
  customerId: string;
}

const eventIcons: Record<string, string> = {
  consent_requested: '📨',
  customer_approved: '✅',
  customer_rejected: '❌',
  customer_opted_out: '🚫',
  consent_manually_approved: '👤',
  optin_resent: '🔄',
  message_blocked: '🚷',
  phone_changed: '📱',
  phone_validation_changed: '🔐',
};

export default function ConsentAuditLog({ customerId }: ConsentAuditLogProps) {
  const { t } = useTranslation();
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getConsentAuditLog(customerId)
      .then((data) => setEvents(data.events || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [customerId]);

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium text-gray-700">
        {t('whatsappConsent.audit.title', 'Consent Audit Log')}
      </h4>
      <div className="max-h-64 overflow-y-auto">
        {loading ? (
          <p className="text-xs text-gray-400 py-2">{t('common.loading', 'Loading...')}</p>
        ) : events.length === 0 ? (
          <p className="text-xs text-gray-400 py-2">{t('whatsappConsent.noAuditEvents', 'No consent events')}</p>
        ) : (
          events.map((event, idx) => (
            <div
              key={idx}
              className="flex items-start gap-2 py-1.5 border-b border-gray-50 last:border-0"
            >
              <span className="text-sm mt-0.5">
                {eventIcons[event.eventType] ?? '📝'}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-700">
                  {t(`whatsappConsent.auditEvents.${event.eventType}`, event.eventType)}
                </p>
                <p className="text-[10px] text-gray-500">
                  {event.previousStatus} → {event.newStatus}
                  {event.triggeredBy && (
                    <span className="ltr:ml-1 rtl:mr-1">
                      ({t(`whatsappConsent.triggeredBy.${event.triggeredBy}`, event.triggeredBy)})
                    </span>
                  )}
                </p>
                {event.messageText && (
                  <p className="text-[10px] text-gray-400 truncate">
                    &quot;{event.messageText}&quot;
                  </p>
                )}
              </div>
              <span className="text-[10px] text-gray-400 whitespace-nowrap">
                {new Date(event.timestamp).toLocaleString()}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
